/**
 * ObservabilityFrontendService — Wave 16
 *
 * Singleton service for emitting frontend-originated spans.
 * Tracks route transitions, plugin interactions, chat submissions, etc.
 *
 * Spans are buffered and flushed to the backend observability endpoint
 * in batches (or to console in dev mode).
 */

import type { FrontendSpan, FrontendSpanKind } from '../types/observability';

// ── ID generation ──────────────────────────────────────

let idCounter = 0;
function generateId(): string {
  return `fe-${Date.now().toString(36)}-${(idCounter++).toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// ── Singleton state ────────────────────────────────────

const activeSpans = new Map<string, FrontendSpan>();
const completedSpans: FrontendSpan[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushEndpoint: string | null = null;
let traceIdOverride: string | null = null;

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 50;
/** Spans someone forgot to end are dropped (oldest first) past this many. */
const MAX_ACTIVE_SPANS = 200;
/** Completed spans kept for retry while the endpoint is failing. */
const MAX_BUFFERED_SPANS = 500;

// ── Public API ─────────────────────────────────────────

/**
 * Initialise the frontend observability service.
 *
 * @param endpoint — Backend URL to POST spans to (e.g. `/api/v1/observability/frontend-spans`)
 */
export function initFrontendObservability(endpoint?: string): void {
  flushEndpoint = endpoint ?? null;

  if (flushTimer) clearInterval(flushTimer);
  flushTimer = setInterval(flushSpans, FLUSH_INTERVAL_MS);

  if (import.meta.env.DEV) {
    console.log('[Observability] Frontend observability initialised', { endpoint });
  }
}

/**
 * Set a trace ID that will be attached to all new spans.
 * Useful when the backend provides a trace context for the current session.
 */
export function setFrontendTraceId(traceId: string | null): void {
  traceIdOverride = traceId;
}

/**
 * Start a new frontend span.
 *
 * @returns The span ID (use to end the span later).
 */
export function startFrontendSpan(
  kind: FrontendSpanKind,
  operationName: string,
  attributes: Record<string, unknown> = {},
  parentSpanId?: string,
): string {
  const spanId = generateId();
  const span: FrontendSpan = {
    spanId,
    traceId: traceIdOverride ?? generateId(),
    parentSpanId,
    kind,
    operationName,
    startTime: Date.now(),
    status: 'in_progress',
    attributes,
  };
  activeSpans.set(spanId, span);
  while (activeSpans.size > MAX_ACTIVE_SPANS) {
    activeSpans.delete(activeSpans.keys().next().value as string);
  }

  if (import.meta.env.DEV) {
    console.debug('[Span:start]', operationName, spanId);
  }

  return spanId;
}

/**
 * End a previously-started span.
 */
export function endFrontendSpan(
  spanId: string,
  status: 'ok' | 'error' = 'ok',
  extraAttributes?: Record<string, unknown>,
): void {
  const span = activeSpans.get(spanId);
  if (!span) {
    if (import.meta.env.DEV) console.warn('[Span:end] Unknown span', spanId);
    return;
  }

  span.endTime = Date.now();
  span.durationMs = span.endTime - span.startTime;
  span.status = status;
  if (extraAttributes) {
    span.attributes = { ...span.attributes, ...extraAttributes };
  }

  activeSpans.delete(spanId);
  completedSpans.push(span);

  if (import.meta.env.DEV) {
    console.debug('[Span:end]', span.operationName, `${span.durationMs}ms`, status);
  }

  // Auto-flush if buffer is large
  if (completedSpans.length >= FLUSH_BATCH_SIZE) {
    flushSpans();
  }
}

/**
 * Convenience: wrap an async function in a span.
 */
export async function withSpan<T>(
  kind: FrontendSpanKind,
  operationName: string,
  fn: () => Promise<T>,
  attributes?: Record<string, unknown>,
): Promise<T> {
  const spanId = startFrontendSpan(kind, operationName, attributes);
  try {
    const result = await fn();
    endFrontendSpan(spanId, 'ok');
    return result;
  } catch (err) {
    endFrontendSpan(spanId, 'error', {
      errorMessage: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}

/**
 * Get all completed spans (for DevTools inspection).
 */
export function getCompletedSpans(): readonly FrontendSpan[] {
  return completedSpans;
}

/**
 * Get currently active spans.
 */
export function getActiveSpans(): readonly FrontendSpan[] {
  return Array.from(activeSpans.values());
}

/**
 * Flush completed spans to the backend.
 */
async function flushSpans(): Promise<void> {
  if (completedSpans.length === 0) return;

  const batch = completedSpans.splice(0, FLUSH_BATCH_SIZE);

  if (!flushEndpoint) {
    // In dev mode without endpoint, just log
    if (import.meta.env.DEV) {
      console.debug('[Observability] Flushing', batch.length, 'spans (no endpoint configured)');
    }
    return;
  }

  try {
    await fetch(flushEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ spans: batch }),
    });
  } catch (err) {
    // Put them back for retry, keeping the newest if the endpoint stays down
    completedSpans.unshift(...batch);
    if (completedSpans.length > MAX_BUFFERED_SPANS) {
      completedSpans.splice(0, completedSpans.length - MAX_BUFFERED_SPANS);
    }
    if (import.meta.env.DEV) {
      console.warn('[Observability] Span flush failed, will retry', err);
    }
  }
}

/**
 * Cleanup (for tests).
 */
export function destroyFrontendObservability(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  activeSpans.clear();
  completedSpans.length = 0;
  flushEndpoint = null;
  traceIdOverride = null;
}
